<script lang="ts" setup>
import { withDefaults, defineProps, computed, useSlots, onMounted } from "vue";
import { TirPmButtonSizeEnum, TirPmButtonVariantEnum } from "./enums";

/**
 * Пропсы
 */
const props = withDefaults(
  defineProps<{
    /**
     * Вариант
     */
    variant?: TirPmButtonVariantEnum;
    /**
     * Размер
     */
    size?: TirPmButtonSizeEnum;
    /**
     * Скругление
     */
    isRounded?: boolean;
    /**
     * Активное состояние
     */
    isActive?: boolean;
    /**
     * Нажатое состояние
     */
    isPressed?: boolean;
    /**
     * Активное состояние закгрузки
     */
    isLoading?: boolean;
    /**
     * Отключен
     */
    isDisabled?: boolean;
  }>(),
  { size: TirPmButtonSizeEnum.Big },
);

/**
 * Открытие слотов
 */
const slots = useSlots();

/**
 * Есть ли слот слева
 */
const isLeft = computed(() => slots && slots.leftSlot && slots.leftSlot());
/**
 * Есть ли слот справа
 */
const isRight = computed(() => slots && slots.rightSlot && slots.rightSlot());
/**
 * Есть ли слот для текста
 */
const isSign = computed(() => slots && slots.default && slots.default());
/**
 * Есть ли слот для текста подсказки
 */
const isHintText = computed(() => slots && slots.hintText && slots.hintText());

/**
 * Выделение классов на основе пропсов
 */
const calculateClasses = computed(() => {
  return {
    "button--rounded": props.isRounded,
    "button--big": props.size == TirPmButtonSizeEnum.Big,
    "button--regular": props.size == TirPmButtonSizeEnum.Regular,
    "button--middle": props.size == TirPmButtonSizeEnum.Middle,
    "button--small": props.size == TirPmButtonSizeEnum.Small,
    "button--primary": props.variant == TirPmButtonVariantEnum.PrimaryState,
    "button--accent": props.variant == TirPmButtonVariantEnum.AccentState,
    "button--link": props.variant == TirPmButtonVariantEnum.LinkState,
    "button--tertiary": props.variant == TirPmButtonVariantEnum.TertiaryState,
    "button--secondary": props.variant == TirPmButtonVariantEnum.SecondaryState,
    "button--link-invert": props.variant == TirPmButtonVariantEnum.LinkInvertState,
    "button--disabled": props.isDisabled,
    "button--only-icon": isOnlyIcon.value,
    "button--active": props.isActive,
    "button--pressed": props.isPressed,
    "button--loading": props.isLoading,
  };
});

/**
 * Проверка, есть ли текст или только иконка
 */
const isOnlyIcon = computed(
  () =>
    !(slots && slots.default && slots.default()) &&
    !(slots && slots.rightSlot && slots.rightSlot()),
);

</script>

<template>
  <button class="button" :class="calculateClasses">
    <div class="button__left-slot" v-if="isLeft && !props.isLoading">
      <slot name="leftSlot"></slot>
    </div>
    <div class="button__sign" v-if="isSign && !props.isLoading">
      <span class="button__sign-main">
        <slot name="default"></slot>
      </span>
      <span class="button__sign-hint" v-if="isHintText && !props.isLoading">
        <slot name="hintText"></slot>
      </span>
    </div>
    <div class="button__right-slot" v-if="isRight && !props.isLoading">
      <slot name="rightSlot"></slot>
    </div>
    <div class="button__loader" v-if="props.isLoading">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path
          d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"
          class="button__loader-circle"
          fill="currentColor"
        />
      </svg>
    </div>
  </button>
</template>

<style lang="scss">
@import "./style.scss";
</style>
